import { isAccountAllowed } from '$lib/components/permission/permission'
import { initAccountImpersonation } from '$lib/domain/userImpersonation/impersonateAccount'
import { quickSetupDone } from '$lib/store/quickSetup'
import { initIntercomAdminLoggedIn, updateIntercomVisibility } from '../intercom'
import { tenantSettings } from './domain/tenantSettings/tenantSettings'
import {
	subscriptionPlanFeatureLimits,
	subscriptionPlanFeatures,
} from './domain/upgrade/checkFeature'
import { onboardingStatus } from './store/onboarding'
import { tenantType } from './store/tenantType'
import { createApi, createStorageApi } from '@helpers/api'
import { auth } from '@helpers/settings'
import { Lang, loadLangFromCms, preselectLang } from '@repo/i18n'
import { signInMethods, user } from '@repo/ui'
import type {
	Api,
	AuthenticationV1ErrorCode,
	ErrorsV1ErrorResponse,
	HttpResponse,
	SettingsV1Firebase,
	SettingsV1Recaptcha,
} from '@sdk/Api'
import {
	EnumsV1FeatureId,
	EnumsV1Permission,
	type SettingsV1FeatureLimit,
	type SettingsV1TenantSettings,
} from '@sdk/Api'
import type { Api as StorageApi } from '@sdk/Storage'
import { accountOrErrorCode } from '@store/account'
import { RegistrationStatus, registrationStatus } from '@store/registrationStatus'
import { timezone, timezoneList } from '@store/timezone'
import { type Auth, type User, fetchSignInMethodsForEmail } from 'firebase/auth'
import { Settings } from 'luxon'
import { locale, register } from 'svelte-i18n'
import { get } from 'svelte/store'

function isFirebaseAuthPersistence() {
	return Object.keys(localStorage).some((key) => key.startsWith('firebase:authUser'))
}

export let nodeApi: Api<Auth>
export let reportsApi: Api<Auth>
export let storageApi: StorageApi<Auth>

export async function updateFeatures() {
	const {
		data: { featureIds, featureLimits },
	} = await nodeApi.settingsV1TenantSettingsService.tenantSettingsServiceGetTenantSettings()
	subscriptionPlanFeatures.set(featureIds)
	subscriptionPlanFeatureLimits.set(featureLimits)
}

export function initializeTracktorApp(
	settings: SettingsV1TenantSettings,
	firebase: SettingsV1Firebase,
	recaptcha: SettingsV1Recaptcha,
	environmentDomain: string,
	featureIds: EnumsV1FeatureId[],
	featureLimits: SettingsV1FeatureLimit[],
) {
	return new Promise<void>((resolve, reject) => {
		try {
			if (featureIds.includes(EnumsV1FeatureId.FEATURE_ID_UI_TRANSLATIONS)) {
				preselectLang(settings.tenantDetail.id)
			}

			const tenantId = settings.tenantDetail.id
			subscriptionPlanFeatures.set(featureIds)
			subscriptionPlanFeatureLimits.set(featureLimits)
			nodeApi = createApi(`https://${tenantId}.${environmentDomain}/api/node`, auth, tenantId)
			reportsApi = createApi(`https://${tenantId}.${environmentDomain}/api/reports`, auth, tenantId)
			storageApi = createStorageApi(
				`https://${tenantId}.${environmentDomain}/api/storage`,
				auth,
				tenantId,
			)

			onboardingStatus.set(settings.onboardingStatus)
			timezone.set(settings.timezone)
			Settings.defaultZone = settings.timezone
			timezoneList.set([settings.timezone])
			tenantType.set(settings.tenantType)

			if (settings.defaultLanguage && settings.defaultLanguage !== Lang.en) {
				register(settings.defaultLanguage, () => {
					return loadLangFromCms(settings.defaultLanguage, tenantId)
				})

				locale.set(settings.defaultLanguage || Lang.en)
				Settings.defaultLocale = settings.defaultLanguage || Lang.en
			}

			if (!isFirebaseAuthPersistence()) {
				resolve()
			}

			auth.onIdTokenChanged(async (userData: User | null) => {
				user.set(userData)
				await onIdTokenChangeObserver(userData, tenantId)
				resolve()
			})
		} catch (e) {
			// TODO: tenant probably does not exist
			console.error(e)
			reject(e)
		}
	})
}

export async function onIdTokenChangeObserver(userData: User | null, tenantId?: string) {
	if (userData && get(registrationStatus) !== RegistrationStatus.InProgress) {
		try {
			const { data } =
				await nodeApi.authenticationV1AuthenticationService.authenticationServiceGetAccount()

			accountOrErrorCode.set(data.account)
			quickSetupDone.set(data.quickSetupDone)
			await initAccountImpersonation()

			if (isAccountAllowed(EnumsV1Permission.PERMISSION_VIEW_INTERCOM)) {
				initIntercomAdminLoggedIn(userData, tenantId, get(locale), get(tenantSettings))
			}

			fetchSignInMethodsForEmail(auth, userData.email).then((methods) => {
				signInMethods.set(methods)
			})
		} catch (e: unknown) {
			const specificError = e as HttpResponse<any, ErrorsV1ErrorResponse>
			const errorCode = specificError.error?.errors[0].code || null
			if (errorCode) {
				accountOrErrorCode.set(errorCode as AuthenticationV1ErrorCode)
			} else {
				console.error(specificError)
			}
		}
	} else {
		accountOrErrorCode.set(null)
		updateIntercomVisibility(null)
	}
}
